import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  createFinancingCoverage,
  fetchFinancingCoverages,
  linkCoverageToScenario,
  unlinkCoverageFromScenario,
  updateFinancingCoverage,
  changeFinancingCoverageStatus,
  deleteFinancingCoverage,
} from "../../services/riskFinancingApi";

export default function useCoveragePortfolio({
  enabled = false,
} = {}) {
  const [coverages, setCoverages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [mutationLoading, setMutationLoading] =
    useState(false);

  const loadCoverages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data =
        await fetchFinancingCoverages();

      const results = Array.isArray(data?.results)
        ? data.results
        : [];

      setCoverages(results);

      return results;
    } catch (requestError) {
      console.error(
        "Coverage Portfolio error:",
        requestError
      );

      setError(requestError);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createCoverage = useCallback(
    async (payload) => {
      try {
        setMutationLoading(true);
        setError(null);

        const createdCoverage =
          await createFinancingCoverage(
            payload
          );

        await loadCoverages();

        return {
          success: true,
          coverage: createdCoverage,
        };
      } catch (requestError) {
        console.error(
          "Create financing coverage error:",
          requestError
        );

        setError(requestError);

        return {
          success: false,
          error: requestError,
        };
      } finally {
        setMutationLoading(false);
      }
    },
    [loadCoverages]
  );

  const updateCoverage = useCallback(
    async (coverageId, payload) => {
      try {
        setMutationLoading(true);
        setError(null);

        const updatedCoverage =
          await updateFinancingCoverage(
            coverageId,
            payload
          );

        await loadCoverages();

        return {
          success: true,
          coverage: updatedCoverage,
        };
      } catch (requestError) {
        console.error(
          "Update financing coverage error:",
          requestError
        );

        setError(requestError);

        return {
          success: false,
          error: requestError,
        };
      } finally {
        setMutationLoading(false);
      }
    },
    [loadCoverages]
  );

  const linkScenario = useCallback(
    async (coverageId, payload) => {
      try {
        setMutationLoading(true);
        setError(null);

        await linkCoverageToScenario(
          coverageId,
          payload
        );

        await loadCoverages();

        return true;
      } catch (requestError) {
        console.error(
          "Link coverage to scenario error:",
          requestError
        );

        setError(requestError);
        return false;
      } finally {
        setMutationLoading(false);
      }
    },
    [loadCoverages]
  );

  const unlinkScenario = useCallback(
    async (coverageId, scenarioId) => {
      try {
        setMutationLoading(true);
        setError(null);

        await unlinkCoverageFromScenario(
          coverageId,
          scenarioId
        );

        await loadCoverages();

        return true;
      } catch (requestError) {
        console.error(
          "Unlink coverage from scenario error:",
          requestError
        );

        setError(requestError);
        return false;
      } finally {
        setMutationLoading(false);
      }
    },
    [loadCoverages]
  );


    const changeCoverageStatus = useCallback(
    async (coverageId, status) => {
      try {
        setMutationLoading(true);
        setError(null);

        await changeFinancingCoverageStatus(
          coverageId,
          status
        );

        await loadCoverages();

        return {
          success: true,
        };
      } catch (requestError) {
        console.error(
          "Change coverage status error:",
          requestError
        );

        setError(requestError);

        return {
          success: false,
          error: requestError,
        };
      } finally {
        setMutationLoading(false);
      }
    },
    [loadCoverages]
  );

  const deleteCoverage = useCallback(
    async (coverageId) => {
      try {
        setMutationLoading(true);
        setError(null);

        await deleteFinancingCoverage(
          coverageId
        );

        await loadCoverages();

        return {
          success: true,
        };
      } catch (requestError) {
        console.error(
          "Delete coverage error:",
          requestError
        );

        setError(requestError);

        return {
          success: false,
          error: requestError,
        };
      } finally {
        setMutationLoading(false);
      }
    },
    [loadCoverages]
  );

  useEffect(() => {
    if (!enabled) return;

    loadCoverages();
  }, [
    enabled,
    loadCoverages,
  ]);

  return {
    coverages,
    loading,
    mutationLoading,
    error,
    refresh: loadCoverages,
    createCoverage,
    updateCoverage,
    changeCoverageStatus,
    deleteCoverage,
    linkScenario,
    unlinkScenario,
  };
}

